import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Users, FileImage, Zap, TrendingUp, MousePointer, MessageSquare, User } from "lucide-react";
import { getCurrentUser } from "../APIs/ProfileApi";
import useAdminDashboard from "../hooks/useAdminDashboard";

// ── Helpers ────────────────────────────────────────────────────────────────────
function pct(today, yesterday) {
  if (!yesterday) return null;
  const d = ((today - yesterday) / yesterday) * 100;
  return d > 0 ? `+${d.toFixed(0)}%` : `${d.toFixed(0)}%`;
}
function fmt(n) { return (n ?? 0).toLocaleString(); }

// ── Skeleton ───────────────────────────────────────────────────────────────────
function Sk({ className = "" }) {
  return <div className={`bg-white/[0.05] animate-pulse rounded-xl ${className}`} />;
}

// ── KPI card ───────────────────────────────────────────────────────────────────
function KpiCard({ label, value, yesterday, Icon }) {
  const change = pct(value, yesterday);
  return (
    <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-4">
      <div className="flex items-start justify-between mb-3">
        <span className="text-white/35 text-[11px] uppercase tracking-wider leading-tight">{label}</span>
        <Icon className="w-3.5 h-3.5 text-white/15 shrink-0" />
      </div>
      <p className="text-white font-bold text-2xl tabular-nums leading-none">{fmt(value)}</p>
      {change && (
        <p className={`text-[11px] mt-1.5 ${change.startsWith('+') ? 'text-white/40' : 'text-white/25'}`}>
          {change} vs yesterday
        </p>
      )}
    </div>
  );
}

// ── 4-line SVG chart ───────────────────────────────────────────────────────────
const LINES = [
  { key: 'new_users',    label: 'New Users',    stroke: 'rgba(255,255,255,0.70)', dash: '' },
  { key: 'new_posts',    label: 'New Posts',    stroke: 'rgba(255,255,255,0.50)', dash: '6 3' },
  { key: 'active_users', label: 'Active Users', stroke: 'rgba(255,255,255,0.35)', dash: '2 4' },
  { key: 'total_events', label: 'Events',       stroke: 'rgba(255,255,255,0.20)', dash: '8 3' },
];

function GrowthChart({ data }) {
  if (!data || data.length < 2) {
    return (
      <div className="h-36 flex items-center justify-center text-white/20 text-sm">
        Not enough data yet
      </div>
    );
  }
  const W = 700, H = 140;
  const PAD = { t: 8, r: 8, b: 28, l: 8 };
  const cW = W - PAD.l - PAD.r;
  const cH = H - PAD.t - PAD.b;

  const maxVal = Math.max(
    ...data.flatMap(d => LINES.map(l => d[l.key] || 0)),
    1,
  );
  const x = i => PAD.l + (i / (data.length - 1)) * cW;
  const y = v => PAD.t + cH - (v / maxVal) * cH;
  const toPath = key =>
    data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d[key] || 0).toFixed(1)}`).join(' ');

  const labelIdxs = [...new Set([0, 6, 13, 20, 27, data.length - 1].filter(i => i < data.length))];

  return (
    <div>
      {/* Legend */}
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mb-3">
        {LINES.map(l => (
          <div key={l.key} className="flex items-center gap-2">
            <svg width="18" height="2">
              <line x1="0" y1="1" x2="18" y2="1"
                stroke={l.stroke} strokeWidth="1.5"
                strokeDasharray={l.dash || undefined} />
            </svg>
            <span className="text-[10px] text-white/35">{l.label}</span>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[320px]" style={{ height: 140 }}>
          {[0.25, 0.5, 0.75, 1].map(p => (
            <line key={p}
              x1={PAD.l} y1={PAD.t + cH * (1 - p)}
              x2={W - PAD.r} y2={PAD.t + cH * (1 - p)}
              stroke="rgba(255,255,255,0.04)" strokeWidth="1"
            />
          ))}
          {LINES.map(l => (
            <path key={l.key} d={toPath(l.key)} fill="none"
              stroke={l.stroke} strokeWidth="1.5"
              strokeDasharray={l.dash || undefined}
              strokeLinecap="round" strokeLinejoin="round"
            />
          ))}
          {labelIdxs.map(i => (
            <text key={i} x={x(i)} y={H - 4} textAnchor="middle"
              fontSize="9" fill="rgba(255,255,255,0.18)">
              {data[i].date.slice(5)}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
}

// ── Category table ─────────────────────────────────────────────────────────────
function CategoryTable({ data }) {
  const maxPosts = Math.max(...data.map(r => r.post_count), 1);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[360px] text-sm">
        <thead>
          <tr className="border-b border-white/[0.06]">
            {['Category', 'Posts', 'Views', 'Clicks'].map(h => (
              <th key={h} className="text-left text-white/30 text-[11px] uppercase tracking-wider
                font-normal pb-2 pr-4 first:pl-1">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.04]">
          {data.map(row => (
            <tr key={row.category} className="group">
              <td className="py-2.5 pl-1 pr-4">
                <div className="flex items-center gap-2">
                  <div
                    className="h-1 rounded-full bg-white/20"
                    style={{ width: `${(row.post_count / maxPosts) * 48}px`, minWidth: 4 }}
                  />
                  <span className="text-white/65 capitalize">{row.category}</span>
                </div>
              </td>
              <td className="py-2.5 pr-4 tabular-nums text-white/60">{fmt(row.post_count)}</td>
              <td className="py-2.5 pr-4 tabular-nums text-white/60">{fmt(row.view_count)}</td>
              <td className="py-2.5 tabular-nums text-white/60">{fmt(row.click_count)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Top creators table ─────────────────────────────────────────────────────────
function CreatorsTable({ data, navigate }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] text-sm">
        <thead>
          <tr className="border-b border-white/[0.06]">
            {['#', 'Creator', 'Posts', 'Views', 'Clicks', 'Followers'].map(h => (
              <th key={h} className="text-left text-white/30 text-[11px] uppercase tracking-wider
                font-normal pb-2 pr-4 first:pl-1">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.04]">
          {data.map((creator, i) => (
            <tr key={creator.user_id}
              className="hover:bg-white/[0.02] cursor-pointer transition-colors"
              onClick={() => navigate(`/profile/${creator.username}`)}>
              <td className="py-2.5 pl-1 pr-4 text-white/25 tabular-nums">{i + 1}</td>
              <td className="py-2.5 pr-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full overflow-hidden bg-white/10 shrink-0">
                    {creator.profile_icon
                      ? <img src={creator.profile_icon} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center">
                          <User className="w-3.5 h-3.5 text-white/20" />
                        </div>
                    }
                  </div>
                  <span className="text-white/70 font-medium">@{creator.username}</span>
                </div>
              </td>
              <td className="py-2.5 pr-4 tabular-nums text-white/55">{fmt(creator.post_count)}</td>
              <td className="py-2.5 pr-4 tabular-nums text-white/55">{fmt(creator.total_views)}</td>
              <td className="py-2.5 pr-4 tabular-nums text-white/55">{fmt(creator.total_clicks)}</td>
              <td className="py-2.5 tabular-nums text-white/55">{fmt(creator.followers_count)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Top posts list ─────────────────────────────────────────────────────────────
function TopPostsList({ data, navigate }) {
  return (
    <div className="divide-y divide-white/[0.04]">
      {data.map((post, i) => (
        <div
          key={post.post_id}
          onClick={() => navigate(`/post/${post.post_id}`)}
          className="flex items-center gap-3 py-3 px-1 cursor-pointer
            hover:bg-white/[0.03] rounded-xl transition-colors"
        >
          <span className="text-white/20 text-xs w-4 shrink-0 tabular-nums">{i + 1}</span>
          <div className="w-10 h-10 rounded-lg overflow-hidden bg-white/[0.06] shrink-0">
            {post.image
              ? <img src={post.image} alt="" className="w-full h-full object-cover"
                  onError={e => { e.target.style.display = 'none'; }} />
              : <div className="w-full h-full flex items-center justify-center">
                  <FileImage className="w-4 h-4 text-white/20" />
                </div>
            }
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white/70 text-sm truncate">{post.caption_snippet || 'Untitled'}</p>
            <p className="text-white/30 text-xs">@{post.author_username}</p>
          </div>
          <div className="flex gap-4 shrink-0">
            {[['views', post.views], ['clicks', post.clicks], ['saves', post.saves]].map(([l, v]) => (
              <div key={l} className="text-center min-w-[28px]">
                <p className="text-white/60 text-sm font-semibold tabular-nums leading-none">{fmt(v)}</p>
                <p className="text-white/25 text-[9px] mt-0.5">{l}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Section wrapper ────────────────────────────────────────────────────────────
function Section({ title, loading, skH = "h-40", children }) {
  return (
    <div className="mb-8">
      <p className="text-white/35 text-[11px] uppercase tracking-wider mb-3">{title}</p>
      <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-4">
        {loading ? <Sk className={skH} /> : children}
      </div>
    </div>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────────
function Empty({ icon: Icon = TrendingUp, text = "No data yet — platform just launched!" }) {
  return (
    <div className="py-10 flex flex-col items-center gap-2">
      <Icon className="w-7 h-7 text-white/15" />
      <p className="text-white/25 text-sm text-center">{text}</p>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);

  useEffect(() => {
    getCurrentUser()
      .then(res => {
        if (res.data.is_superuser) setAuthorized(true);
        else { setAuthorized(false); navigate('/home', { replace: true }); }
      })
      .catch(() => { setAuthorized(false); navigate('/home', { replace: true }); });
  }, [navigate]);

  const { summary, categories, creators, topPosts, timeseries, loading, error } =
    useAdminDashboard(authorized === true);

  if (authorized === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-3">
        <Shield className="w-8 h-8 text-white/20" />
        <p className="text-white/40 text-sm">{error}</p>
        <button onClick={() => window.location.reload()}
          className="text-white/30 hover:text-white text-xs underline transition">Retry</button>
      </div>
    );
  }

  const s = summary || {};

  return (
    <div className="min-h-screen bg-black text-white px-4 pt-6 pb-24">

      {/* Header */}
      <div className="mb-7 flex items-center gap-3">
        <Shield className="w-5 h-5 text-white/30" />
        <div>
          <h1 className="text-xl font-bold text-white leading-none">Admin Dashboard</h1>
          <p className="text-white/30 text-sm mt-0.5">Platform health &amp; metrics</p>
        </div>
      </div>

      {/* ── KPI cards — 2 rows of 4 ── */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[...Array(8)].map((_, i) => <Sk key={i} className="h-24" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <KpiCard label="Total Users"   value={s.total_users}     yesterday={null}              Icon={Users} />
            <KpiCard label="New Today"     value={s.new_users_today} yesterday={s.new_users_yesterday} Icon={Users} />
            <KpiCard label="Total Posts"   value={s.total_posts}     yesterday={null}              Icon={FileImage} />
            <KpiCard label="Posts Today"   value={s.posts_today}     yesterday={s.posts_yesterday} Icon={FileImage} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <KpiCard label="Active Today"  value={s.active_users_today} yesterday={s.active_users_yesterday} Icon={Zap} />
            <KpiCard label="Active / Week" value={s.active_users_week}  yesterday={null}                     Icon={TrendingUp} />
            <KpiCard label="DM Shares"     value={s.total_dm_shares}    yesterday={null}                     Icon={MessageSquare} />
            <KpiCard label="Link Clicks"   value={s.total_link_clicks}  yesterday={null}                     Icon={MousePointer} />
          </div>
        </>
      )}

      {/* ── Growth chart ── */}
      <Section title="30-Day Growth" loading={loading} skH="h-44">
        {timeseries.length > 0
          ? <GrowthChart data={timeseries} />
          : <Empty icon={TrendingUp} />
        }
      </Section>

      {/* ── Category breakdown ── */}
      <Section title="Category Breakdown" loading={loading} skH="h-48">
        {categories.length > 0
          ? <CategoryTable data={categories} />
          : <Empty icon={FileImage} text="No posts yet" />
        }
      </Section>

      {/* ── Top creators ── */}
      <Section title="Top Creators" loading={loading} skH="h-52">
        {creators.length > 0
          ? <CreatorsTable data={creators} navigate={navigate} />
          : <Empty icon={Users} text="No creator data yet" />
        }
      </Section>

      {/* ── Top posts ── */}
      <Section title="Top Posts — Last 7 Days" loading={loading} skH="h-52">
        {topPosts.length > 0
          ? <TopPostsList data={topPosts} navigate={navigate} />
          : <Empty icon={FileImage} text="No post activity yet" />
        }
      </Section>

    </div>
  );
}
